import type { PriceRangeInterval } from '@typings/search';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import React, {
	useEffect,
	useRef,
	useCallback,
	useMemo,
	useState,
} from 'react';
import { BarChart } from './BarChart';
import Styles from './styles.scss';

interface RangeSliderProps {
	step: number;
	startingMin: number;
	startingMax: number;
	minVal: number;
	maxVal: number;
	setMinVal: (value: number) => void;
	setMaxVal: (value: number) => void;
	maxName?: string;
	shouldShowErrors?: boolean;
	shouldShowLabels?: boolean;
	shouldShowSteps?: boolean;
	intervals?: PriceRangeInterval[];
}

export const RangeSlider = ({
	startingMin,
	startingMax,
	minVal,
	maxVal,
	setMinVal,
	setMaxVal,
	step,
	maxName,
	shouldShowErrors = false,
	shouldShowLabels = false,
	shouldShowSteps = false,
	intervals,
}: RangeSliderProps) => {
	const minValRef = useRef<HTMLInputElement | null>(null);
	const maxValRef = useRef<HTMLInputElement | null>(null);
	const range = useRef<HTMLDivElement | null>(null);
	const numSteps = startingMax / step + 1;
	const [localMinVal, setLocalMinVal] = useState(minVal);
	const [localMaxVal, setLocalMaxVal] = useState(maxVal);

	useEffect(() => setLocalMinVal(minVal), [minVal]);
	useEffect(() => setLocalMaxVal(maxVal), [maxVal]);

	const debouncedSetMinVal = useMemo(
		() => debounce(setMinVal, 250),
		[setMinVal],
	);
	const debouncedSetMaxVal = useMemo(
		() => debounce(setMaxVal, 250),
		[setMaxVal],
	);

	// Flush debounced calls on unmount to ensure the latest values are set
	useEffect(
		() => () => {
			debouncedSetMinVal.flush();
			debouncedSetMaxVal.flush();
		},
		[debouncedSetMinVal, debouncedSetMaxVal],
	);

	// Values outside [startingMin, startingMax] are bounded to [0%, 100%] to prevent overflow
	const getPercentInsideRange = useCallback(
		(value: number) => {
			const boundedValue = Math.min(Math.max(value, startingMin), startingMax);
			return Math.round(
				((boundedValue - startingMin) / (startingMax - startingMin)) * 100,
			);
		},
		[startingMin, startingMax],
	);

	// Update the visual range bar when values change
	useEffect(() => {
		if (range.current) {
			const minPercent = getPercentInsideRange(localMinVal);
			const maxPercent = getPercentInsideRange(localMaxVal);
			range.current.style.left = `${minPercent}%`;
			range.current.style.width = `${maxPercent - minPercent}%`;
		}
	}, [localMinVal, localMaxVal, getPercentInsideRange]);

	const onChangeFromRange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number.parseInt(event.target.value);
		const inRangeValue = value >= localMaxVal ? localMaxVal - step : value;
		setLocalMinVal(inRangeValue);
		(intervals ? debouncedSetMinVal : setMinVal)(inRangeValue);
	};

	const onChangeToRange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number.parseInt(event.target.value);
		const inRangeValue = value <= localMinVal ? localMinVal + step : value;
		setLocalMaxVal(inRangeValue);
		(intervals ? debouncedSetMaxVal : setMaxVal)(inRangeValue);
	};

	return (
		<div
			className={classNames(Styles.rangeContainer, Styles.rangeContainerLite)}
			style={
				{
					'--leftVal': localMinVal,
					'--rightVal': localMaxVal,
					'--minVal': startingMin,
					'--maxVal': startingMax,
					'--numSteps': numSteps,
				} as React.CSSProperties
			}
		>
			{intervals && (
				<BarChart
					intervals={intervals}
					minVal={localMinVal}
					maxVal={localMaxVal}
				/>
			)}
			<div className={Styles.slidersWrapperLite}>
				{shouldShowLabels && (
					<output
						className={classNames(
							Styles.leftLabel,
							shouldShowErrors && Styles.hasError,
							localMinVal === startingMin && Styles.minLeftLabel,
						)}
					/>
				)}
				<input
					data-testid="min-range-slider"
					type="range"
					ref={minValRef}
					min={startingMin}
					max={startingMax}
					value={localMinVal}
					step={step}
					className={classNames(
						Styles.thumb,
						Styles.thumbMin,
						(!shouldShowLabels || !intervals) && Styles.hasBackground,
						Styles.thumbLite,
					)}
					onChange={onChangeFromRange}
					onMouseUp={(e) => e.stopPropagation()}
					tabIndex={-1}
				/>
				{shouldShowLabels && (
					<output
						className={classNames(
							Styles.rightLabel,
							shouldShowErrors && Styles.hasError,
							localMaxVal === startingMax && Styles.maxRightLabel,
						)}
					/>
				)}
				<input
					data-testid="max-range-slider"
					type="range"
					ref={maxValRef}
					min={startingMin}
					max={startingMax}
					value={localMaxVal}
					step={step}
					className={classNames(
						Styles.thumb,
						Styles.thumbMax,
						(!shouldShowLabels || !intervals) && Styles.hasBackground,
						Styles.thumbLite,
					)}
					onChange={onChangeToRange}
					onMouseUp={(e) => e.stopPropagation()}
					tabIndex={-1}
					name={maxName}
				/>
				<div
					className={classNames(
						Styles.slider,
						shouldShowSteps && Styles.showSteps,
					)}
				>
					<div
						className={classNames(Styles.sliderTrack, Styles.sliderTrackLite)}
					/>
					<div
						ref={range}
						className={classNames(
							Styles.sliderRange,
							shouldShowLabels &&
								localMinVal === startingMin &&
								localMaxVal === startingMax &&
								Styles.noRangeSlider,
							Styles.sliderRangeLite,
						)}
					/>
				</div>
			</div>
		</div>
	);
};
